/**
 * Add Slides Tool Schema
 *
 * Schema for adding new slides via simple COM operations:
 * - addEmpty: insert blank slide with layout
 * - copySlide: copy existing slide (optionally clearing content)
 */

import { z } from "zod";
import {
    AddEmptyOperationSchema,
    CopySlideOperationSchema,
} from "./operations.schema";

// =============================================================================
// OPERATION SCHEMAS
// =============================================================================

export const AddSlidesOperationSchema = z.union([
    z.object({ addEmpty: AddEmptyOperationSchema }),
    z.object({ copySlide: CopySlideOperationSchema }),
]);

// =============================================================================
// MAIN PARAMS SCHEMA
// =============================================================================

export const AddSlidesParamsSchema = z.object({
    uiDescription: z.string()
        .describe("Brief description (max ~15 words) of what this tool call is attempting."),
    operations: z.array(AddSlidesOperationSchema)
        .min(1)
        .describe("Array of slide operations to execute in order"),
});

// =============================================================================
// RESULT SCHEMA (for typing/documentation)
// =============================================================================

export const OperationResultSchema = z.object({
    operationType: z.enum(["addEmpty", "copySlide"]),
    position: z.number().int(),
    slideId: z.string(),
    status: z.enum(["completed", "failed"]),
    error: z.string().optional(),
});

export const AddSlidesResultSchema = z.object({
    success: z.boolean(),
    slidesCreated: z.number().int(),
    operations: z.array(OperationResultSchema),
    metadata: z.object({
        totalTime: z.number(),
        slidesAdded: z.number().int(),
        slidesCopied: z.number().int(),
    }),
    message: z.string().optional(),
});

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type AddSlidesOperation = z.infer<typeof AddSlidesOperationSchema>;
export type AddSlidesParams = z.infer<typeof AddSlidesParamsSchema>;
export type OperationResult = z.infer<typeof OperationResultSchema>;
export type AddSlidesResult = z.infer<typeof AddSlidesResultSchema>;
